<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useCaseAdminStore } from '@/views/admin/adminStore';
    import { logger } from '@/util';
    import type { Template } from '@/domain/template/template';

    const props = defineProps<{ template: Template }>();
    const log = logger();
    const store = useCaseAdminStore();

    const state = ref<'hidden' | 'showing' | 'working' | 'error' | 'complete'>('hidden');
    const resultUrl = ref<string>('');
    const showCreateButton = computed(() => ['showing', 'working', 'error'].includes(state.value));

    const isVisible = computed({
        get: () => {
            return state.value !== 'hidden';
        },
        set: (value: boolean) => {
            if (value) {
                state.value = 'showing';
            } else {
                state.value = 'hidden';
            }
        },
    });

    const onCreate = async () => {
        try {
            state.value = 'working';
            const result = await store.duplicateTemplate(props.template);
            state.value = 'complete';
            resultUrl.value = result;
        } catch (e) {
            log.error('Error while duplicating template: %s', e);
            state.value = 'error';
        }
    };
</script>

<template>
    <app-modal v-model="isVisible" width="600">
        <template #activator="{ props: activatorProps }">
            <v-btn
                height="30"
                class="px-5"
                color="primary"
                variant="outlined"
                rounded
                v-bind="activatorProps"
            >
                Rerun
            </v-btn>
        </template>

        <v-card min-height="400px" color="d-flex-column fill-height">
            <app-modal-title class="headline" @close="isVisible = false">
                Rerun Templating
            </app-modal-title>
            <app-modal-content v-if="state === 'complete'">
                <div class="d-flex ga-2 align-center">
                    <v-icon icon="mdi-check-circle" size="large" color="success" />
                    <p>Started rerunning as template <a href="{{resultUrl}}">{{resultUrl}}</a></p>
                </div>
            </app-modal-content>
            <app-modal-content v-else-if="state === 'error'">
                <div class="d-flex ga-2 align-center">
                    <v-icon icon="mdi-alert-circle" size="large" color="error" />
                    <p>Error while attempting to rerun study - see logs</p>
                </div>
            </app-modal-content>
            <app-modal-content v-else>
                <p class="mb-4">
                    This will rerun the automated-templating process using same specifications and
                    study as as template {{ template.id.template }}.
                </p>
                <p>When the process completes the result will become the new automated-template.</p>
            </app-modal-content>
            <v-spacer />
            <app-modal-content class="align-self-end">
                <app-button-primary
                    v-if="showCreateButton"
                    :loading="state === 'working'"
                    @click="onCreate"
                >
                    Create
                </app-button-primary>
            </app-modal-content>
        </v-card>
    </app-modal>
</template>

<style scoped lang="scss">
a {
  color: rgb(var(--v-theme-primary));
}
</style>
